import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Save, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { InquiryItem } from "@/types/database";

export function InquiryItineraryEditView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State to track edited values
  const [itineraryValues, setItineraryValues] = useState<{
    [day: string]: {
      itinerary: string;
    };
  }>({});

  // Fetch inquiry data
  const { data: inquiry, isLoading: isInquiryLoading } = useQuery<InquiryItem>({
    queryKey: ["inquiry", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("inquiry")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as InquiryItem;
    },
  });

  // Initialize edited values when data is loaded
  useEffect(() => {
    if (inquiry && inquiry.tour_package_json) {
      const initialValues: typeof itineraryValues = {};
      
      Object.entries(inquiry.tour_package_json || {}).forEach(([day, dayData]: [string, any]) => {
        initialValues[day] = {
          itinerary: dayData.itinerary || '',
        };
      });
      
      setItineraryValues(initialValues);
    }
  }, [inquiry]);

  // Mutation to update inquiry data
  const updateInquiry = useMutation({
    mutationFn: async (updatedData: any) => {
      const { error } = await supabase
        .from("inquiry")
        .update(updatedData)
        .eq("id", id);

      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inquiry", id] });
      toast.success("Itinerary updated successfully");
      navigate(`/inquiry/${id}/itinerary`);
    },
    onError: (error: Error) => {
      toast.error(`Error saving changes: ${error.message}`);
    },
  });

  const handleItineraryChange = (day: string, value: string) => {
    setItineraryValues((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        itinerary: value,
      },
    }));
  };

  const handleSave = () => {
    if (!inquiry) return;

    // Create a deep copy of the tour_package_json
    const updatedTourPackageJson = JSON.parse(JSON.stringify(inquiry.tour_package_json || {}));
    
    // Update with edited values
    Object.entries(itineraryValues).forEach(([day, dayData]) => {
      if (updatedTourPackageJson[day]) {
        updatedTourPackageJson[day] = {
          ...updatedTourPackageJson[day],
          itinerary: dayData.itinerary,
        };
      }
    });

    // Update inquiry data
    updateInquiry.mutate({
      tour_package_json: updatedTourPackageJson,
    });
  };

  if (isInquiryLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (!inquiry) {
    return <div>Inquiry not found</div>;
  }

  return (
    <div className="container mx-auto py-2 space-y-2 max-w-[210mm]">
      <div className="flex justify-between items-center">
        <Button 
          variant="outline" 
          onClick={() => navigate(`/inquiry/${id}/itinerary`)}
          className="print:hidden"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to View
        </Button>
        <h1 className="text-xl font-bold">Itinerary Edit Mode</h1>
        <Button 
          onClick={handleSave}
          disabled={updateInquiry.isPending}
          className="print:hidden"
        >
          <Save className="w-4 h-4 mr-2" />
          Save Changes
        </Button>
      </div>

      <Card className="shadow-sm p-4 bg-primary/5">
        <CardContent className="p-0 flex justify-center">
          <h2 className="text-3xl font-bold text-primary">Voucher Code: {inquiry.voucher_code}</h2>
        </CardContent>
      </Card>

      <Card className="shadow-sm p-1">
        <CardHeader className="p-1">
          <CardTitle className="text-sm font-medium">Guest Information</CardTitle>
        </CardHeader>
        <CardContent className="p-1">
          <Table className="border-collapse border border-gray-800">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-4 p-2 text-xs border border-gray-800">Name</TableHead>
                <TableHead className="h-4 p-2 text-xs border border-gray-800">Guest</TableHead>
                <TableHead className="h-4 p-2 text-xs border border-gray-800">Days</TableHead>
                <TableHead className="h-4 p-2 text-xs border border-gray-800">Date</TableHead>
                <TableHead className="h-4 p-2 text-xs border border-gray-800">Flight Information</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="hover:bg-transparent">
                <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.name}</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800">
                  {inquiry.adult_count} Adult & {inquiry.child_count} Child
                </TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.nights}</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.date ? format(new Date(inquiry.date), "dd MMM yyyy") : "-"}</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800 whitespace-pre-line">{inquiry.flight_information ?? "-"}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Tour Itinerary Information - Editable */}
      {inquiry.tour_package_json && Object.entries(inquiry.tour_package_json).length > 0 && (
        <Card className="shadow-sm p-1">
          <CardHeader className="p-1">
            <CardTitle className="text-sm font-medium">Tour Itinerary Information (Editable)</CardTitle>
          </CardHeader>
          <CardContent className="p-1">
            <div className="space-y-4">
              {Object.entries(itineraryValues).map(([day, data]) => {
                const tourPackageData = inquiry.tour_package_json[day];
                return (
                  <div key={day} className="p-2 border border-gray-800 rounded-md">
                    <h3 className="text-xs font-medium mb-2">
                      Day {day.split('_')[1]} - {(tourPackageData as any)?.day_date ? String((tourPackageData as any).day_date) : ''}
                    </h3>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs font-bold">{(tourPackageData as any)?.tour_package_name ? String((tourPackageData as any).tour_package_name) : ''}</p>
                      </div>
                      <div>
                        <Textarea
                          className="text-xs min-h-24"
                          value={data.itinerary}
                          onChange={(e) => handleItineraryChange(day, e.target.value)}
                          placeholder="Enter itinerary details here..."
                          style={{ lineHeight: '2em' }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Action Buttons */}
      <div className="flex justify-end gap-4 mt-8 print:hidden sticky bottom-0 w-full bg-white border-t shadow-lg p-4">
        <Button 
          variant="outline" 
          onClick={() => navigate(`/inquiry/${id}/itinerary`)}
          className="print:hidden"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          disabled={updateInquiry.isPending}
          className="print:hidden"
        >
          <Save className="w-4 h-4 mr-2" />
          Save Changes
        </Button>
      </div>
    </div>
  );
}
